import { activeCampaign } from "../../campaign/index.js";
/**
 * Grid layout and canvas ↔ cell coordinate conversion.
 * Shared by canvas-render-adapter (drawing) and input-adapter (click → Cell).
 */

import type { Cell } from "../../domain/types.js";

export interface GridLayout {
  cellW: number;
  cellH: number;
  offsetX: number;
  offsetY: number;
  /** Legacy: for square grids, returns cellW. */
  cellSize: number;
}

/** Game scene: grid occupies the full canvas (HUD moved to DOM in Phase 2+3). */
export const GAME_GRID_W = 1280;
export const GAME_GRID_H = 720;
const GAME_GRID_MIN_LEFT_GUTTER = 64;
const GAME_GRID_MAX_LEFT_GUTTER = 112;
const GAME_GRID_LEFT_GUTTER_FRACTION = 0.25;

/** HUD panel (right of game grid). Layout must match canvas-render-adapter. */
export const HUD_X = GAME_GRID_W;
const HUD_W = 200;
const HUD_PAD = 15;
const HUD_BAR_W = HUD_W - 30;
const SPEED_BTN_Y = 20 + 18 + 14 + 15 + 18; // after MANA label, bar, SPEED label
const SPEED_BTN_W = Math.floor((HUD_BAR_W - 10) / 3);
const SPEED_BTN_H = 20;
const SPEED_BTN_GAP = 5;

/** Map scene: title occupies the top, grid fills the rest. */
export const MAP_TITLE_H = 45;
export const MAP_BOTTOM_PAD = 10;

/**
 * Compute grid layout: square cells, centered on canvas.
 * Precondition: rows > 0, cols > 0, canvasWidth > 0, canvasHeight > 0.
 * If not met, returns a safe default layout (no division by zero).
 */
export function getGridLayout(
  canvasWidth: number,
  canvasHeight: number,
  rows: number,
  cols: number
): GridLayout {
  if (
    rows <= 0 ||
    cols <= 0 ||
    canvasWidth <= 0 ||
    canvasHeight <= 0
  ) {
    return { cellW: 1, cellH: 1, cellSize: 1, offsetX: 0, offsetY: 0 };
  }
  const cellSize = Math.min(
    canvasWidth / cols,
    canvasHeight / rows
  );
  const offsetX = (canvasWidth - cols * cellSize) / 2;
  const offsetY = (canvasHeight - rows * cellSize) / 2;
  return { cellW: cellSize, cellH: cellSize, cellSize, offsetX, offsetY };
}

/**
 * Convert canvas coordinates to cell, or null if outside grid.
 * Returns null when grid is invalid (rows/cols zero or negative).
 */
export function pointToCell(
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number,
  rows: number,
  cols: number
): Cell | null {
  if (rows <= 0 || cols <= 0) return null;
  const { cellW, cellH, offsetX, offsetY } = getGridLayout(
    canvasWidth,
    canvasHeight,
    rows,
    cols
  );
  const c = (canvasX - offsetX) / cellW;
  const r = (canvasY - offsetY) / cellH;
  const col = Math.floor(c);
  const row = Math.floor(r);
  if (
    row >= 0 &&
    row < rows &&
    col >= 0 &&
    col < cols
  ) {
    return { row, col };
  }
  return null;
}

/**
 * Cell center in canvas coordinates (for drawing towers).
 */
export function cellToCanvas(
  row: number,
  col: number,
  layout: GridLayout
): { x: number; y: number } {
  return {
    x: layout.offsetX + (col + 0.5) * layout.cellW,
    y: layout.offsetY + (row + 0.5) * layout.cellH,
  };
}

/** Grid layout for the game scene (left 600×550 area). */
export function getGameGridLayout(rows: number, cols: number): GridLayout {
  const layout = getGridLayout(GAME_GRID_W, GAME_GRID_H, rows, cols);
  if (rows <= 0 || cols <= 0) return layout;

  const gridW = cols * layout.cellW;
  const spareX = GAME_GRID_W - gridW;
  if (spareX <= 0) return layout;

  const biasedOffsetX = Math.min(
    layout.offsetX,
    Math.max(
      GAME_GRID_MIN_LEFT_GUTTER,
      Math.min(GAME_GRID_MAX_LEFT_GUTTER, spareX * GAME_GRID_LEFT_GUTTER_FRACTION)
    )
  );

  return { ...layout, offsetX: biasedOffsetX };
}

/**
 * Pack map cell centers closer (still inside the original 13×13 bbox on the art).
 * Tighter spacing lets hex silhouettes nearly meet without retuning landmark offsets.
 */
export const MAP_GRID_PACK = 0.93;

/** Whole-cell nudge after packing: negative = one column to the left on the parchment. */
export const MAP_GRID_SHIFT_COLS = -1;

/** Fraction of cell height: move grid up (smaller offsetY) to expose another row at top/bottom. */
export const MAP_GRID_SHIFT_ROWS_UP = 0.45;

/** Grid layout for the map scene (stretched to match parchment art). */
export function getMapGridLayout(canvasWidth: number, canvasHeight: number): GridLayout {
  // Base tuning from in-browser measurements (B11, K11, G1); MAP_GRID_PACK shrinks pitch in-place.
  const cellW0 = (canvasWidth / 1280) * 82;
  const cellH0 = (canvasHeight / 720) * 48;
  const offsetX0 = (canvasWidth / 1280) * 235;
  const offsetY0 = (canvasHeight / 720) * 38;
  const cellW = cellW0 * MAP_GRID_PACK;
  const cellH = cellH0 * MAP_GRID_PACK;
  const gridW0 = 13 * cellW0;
  const gridH0 = 13 * cellH0;
  let offsetX = offsetX0 + (gridW0 - 13 * cellW) / 2;
  let offsetY = offsetY0 + (gridH0 - 13 * cellH) / 2;
  offsetX += MAP_GRID_SHIFT_COLS * cellW;
  offsetY -= MAP_GRID_SHIFT_ROWS_UP * cellH;
  return {
    cellW,
    cellH,
    offsetX,
    offsetY,
    cellSize: cellW,
  };
}

/** Convert canvas click to game-grid cell (game scene). */
export function pointToGameCell(
  canvasX: number,
  canvasY: number,
  rows: number,
  cols: number
): Cell | null {
  if (rows <= 0 || cols <= 0) return null;
  if (activeCampaign.ui?.theme === "modern") {
    const layout = getGameGridLayout(rows, cols);
    return pointToIsoCell(canvasX, canvasY, rows, cols, layout);
  }
  const { cellW, cellH, offsetX, offsetY } = getGameGridLayout(rows, cols);
  const c = (canvasX - offsetX) / cellW;
  const r = (canvasY - offsetY) / cellH;
  const col = Math.floor(c);
  const row = Math.floor(r);
  if (row >= 0 && row < rows && col >= 0 && col < cols) {
    return { row, col };
  }
  return null;
}

/**
 * Convert canvas click to speed button (0 | 1 | 3) or null if outside.
 * Game scene only; HUD panel. Matches canvas-render-adapter layout.
 */
export function pointToSpeedButton(
  canvasX: number,
  canvasY: number
): 0 | 1 | 3 | null {
  const x = HUD_X + HUD_PAD;
  if (canvasX < x || canvasX >= x + HUD_BAR_W) return null;
  const by = SPEED_BTN_Y;
  if (canvasY < by || canvasY >= by + SPEED_BTN_H) return null;
  const relX = canvasX - x;
  const idx = Math.floor(relX / (SPEED_BTN_W + SPEED_BTN_GAP));
  if (idx < 0 || idx > 2) return null;
  const btnLeft = idx * (SPEED_BTN_W + SPEED_BTN_GAP);
  if (relX > btnLeft + SPEED_BTN_W) return null;
  return (idx === 0 ? 0 : idx === 1 ? 1 : 3) as 0 | 1 | 3;
}

/** Inventory bar layout (matches canvas-render-adapter drawInventory). */
export const INV_Y = GAME_GRID_H;
export const INV_H = 50;
const INV_GEM_SIZE = 30;
const INV_GAP = 5;
const INV_START_X = 95;

export interface GemView {
  id: string;
  type: string;
  level: number;
}

export interface TowerView {
  id: string;
  cell: { row: number; col: number };
  gemSlots: Array<{ gem: { type: string; level: number } | null }>;
}

/**
 * Hit test: canvas point to inventory slot. Returns gem index and gem view or null.
 */
export function pointToInventorySlot(
  canvasX: number,
  canvasY: number,
  gemInventory: readonly GemView[]
): { gemIndex: number; gem: GemView } | null {
  if (canvasY < INV_Y || canvasY >= INV_Y + INV_H) return null;
  const topPad = INV_Y + (INV_H - INV_GEM_SIZE) / 2;
  if (canvasY < topPad || canvasY >= topPad + INV_GEM_SIZE) return null;
  if (canvasX < INV_START_X) return null;
  const relX = canvasX - INV_START_X;
  const slotWidth = INV_GEM_SIZE + INV_GAP;
  const idx = Math.floor(relX / slotWidth);
  if (idx < 0 || idx >= gemInventory.length) return null;
  const slotLeft = idx * slotWidth;
  if (relX < slotLeft || relX >= slotLeft + INV_GEM_SIZE) return null;
  const gem = gemInventory[idx];
  if (!gem) return null;
  return { gemIndex: idx, gem };
}

/**
 * Hit test: canvas point to tower slot. One slot per tower (slotIndex=0).
 */
export function pointToTowerSlot(
  canvasX: number,
  canvasY: number,
  rows: number,
  cols: number,
  towers: readonly TowerView[]
): { towerId: string; slotIndex: 0 } | null {
  const cell = pointToGameCell(canvasX, canvasY, rows, cols);
  if (!cell) return null;
  const tower = towers.find(
    (t) => t.cell.row === cell.row && t.cell.col === cell.col
  );
  if (!tower) return null;
  return { towerId: tower.id, slotIndex: 0 };
}

export type DropZoneResult =
  | { kind: "inventory" }
  | { kind: "tower"; towerId: string; slotIndex: 0 }
  | null;

/**
 * Hit test: canvas point to valid drop zone (inventory or tower slot).
 */
export function pointToDropZone(
  canvasX: number,
  canvasY: number,
  rows: number,
  cols: number,
  towers: readonly TowerView[],
  _gemInventory: readonly GemView[]
): DropZoneResult {
  if (canvasY >= INV_Y && canvasY < INV_Y + INV_H) {
    return { kind: "inventory" };
  }
  const towerHit = pointToTowerSlot(canvasX, canvasY, rows, cols, towers);
  if (towerHit) return { kind: "tower", towerId: towerHit.towerId, slotIndex: 0 };
  return null;
}

/** Inset from cell rect when fitting a pointy-top hex (must match map draw). */
export const MAP_HEX_INSET = 0;

/** Pointy-top hex circumradius (center → vertex) inscribed in each map cell rect. */
export function getMapHexCircumradius(layout: GridLayout): number {
  const w = Math.max(4, layout.cellW - MAP_HEX_INSET * 2);
  const h = Math.max(4, layout.cellH - MAP_HEX_INSET * 2);
  return Math.min(h / 2, w / Math.sqrt(3));
}

/**
 * Pointy-top hex hit test; `dx,dy` offset from cell center, canvas y downward.
 */
export function mapPointInHexCell(dx: number, dy: number, R: number): boolean {
  if (R <= 0) return false;
  const ady = Math.abs(dy);
  if (ady > R + 1e-6) return false;
  const halfW = ((R - ady) * Math.sqrt(3)) / 2;
  return Math.abs(dx) <= halfW + 1e-6;
}

/** Convert canvas click to map-grid cell (map scene); cells are pointy-top hexes. */
export function pointToMapCell(
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number,
  rows = 13,
  cols = 13
): Cell | null {
  const layout = getMapGridLayout(canvasWidth, canvasHeight);
  const { cellW, cellH, offsetX, offsetY } = layout;
  const R = getMapHexCircumradius(layout);
  const col0 = Math.floor((canvasX - offsetX) / cellW);
  const row0 = Math.floor((canvasY - offsetY) / cellH);
  let best: Cell | null = null;
  let bestD2 = Infinity;
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const row = row0 + dr;
      const col = col0 + dc;
      if (row < 0 || row >= rows || col < 0 || col >= cols) continue;
      const center = cellToCanvas(row, col, layout);
      const dx = canvasX - center.x;
      const dy = canvasY - center.y;
      if (!mapPointInHexCell(dx, dy, R)) continue;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = { row, col };
      }
    }
  }
  return best;
}


/**
 * 2.5D Isometric cell center calculation for modern theme.
 */
export function cellToIsoCanvas(
  row: number,
  col: number,
  layout: GridLayout,
  _rows: number = 13,
  _cols: number = 13
): { x: number; y: number } {
  const tileW = layout.cellW * 1.35;
  const tileH = layout.cellH * 0.65;
  const originX = GAME_GRID_W * 0.5;
  const originY = GAME_GRID_H * 0.22;
  const isoX = originX + (col - row) * (tileW / 2);
  const isoY = originY + (col + row) * (tileH / 2);
  return { x: isoX, y: isoY };
}

/**
 * Convert canvas point to 2.5D isometric cell.
 */
export function pointToIsoCell(
  canvasX: number,
  canvasY: number,
  rows: number,
  cols: number,
  layout: GridLayout
): Cell | null {
  const tileW = layout.cellW * 1.35;
  const tileH = layout.cellH * 0.65;
  const originX = GAME_GRID_W * 0.5;
  const originY = GAME_GRID_H * 0.22;
  const dx = canvasX - originX;
  const dy = canvasY - originY;
  const colFloat = (dx / (tileW / 2) + dy / (tileH / 2)) / 2;
  const rowFloat = (dy / (tileH / 2) - dx / (tileW / 2)) / 2;
  const col = Math.floor(colFloat + 0.5);
  const row = Math.floor(rowFloat + 0.5);
  if (row >= 0 && row < rows && col >= 0 && col < cols) {
    return { row, col };
  }
  return null;
}
